import { createContext, useContext, useEffect, useReducer, useRef, type Dispatch } from 'react';

import { useAuth } from '@/context/auth.context';
import { useProfileSettings } from '@/context/profileSettings.context';
import { fetchPeriodData } from '@/lib/api/fetchPeriodData';
import { resetPersistedFilterOptions } from '@/lib/filters/resetPersistedFilters';
import { persistOnLS } from '@/lib/localStorage';
import { isEmptyValue } from '@/utils';
import { toLatestYearDateRange } from '@/utils/format';
import type { DateRange } from '@/utils/globalTypes';

import { filterInitialState as initState } from './constants';
import { CONTEXT_TYPES, FILTER_ACTION_TYPES } from './enums';

import type {
  FilterAction,
  FiltersProviderProps,
  FilterState,
  Filters,
  ContextKey,
  ActiveSelectors,
  View,
} from './types';

export const FiltersContext = createContext<FilterState | null>(null);
export const FiltersDispatchContext = createContext<Dispatch<FilterAction> | null>(null);

const clean = (obj: Filters): Filters => {
  const next: Filters = { ...obj };
  (Object.keys(next) as Array<keyof Filters>).forEach((k) => {
    if (isEmptyValue(next[k])) delete next[k];
  });
  return next;
};

const evaluate = (obj: Filters): ActiveSelectors => {
  const out: ActiveSelectors = {};
  (Object.keys(obj) as Array<keyof Filters>).forEach((k) => {
    out[k] = !isEmptyValue(obj[k]);
  });
  return out;
};

const filterReducer: React.Reducer<FilterState, FilterAction> = (state, action) => {
  if (!action) return state;

  switch (action?.type) {
    case FILTER_ACTION_TYPES.UPDATE: {
      const context: ContextKey = action?.context ?? CONTEXT_TYPES.GLOBAL;
      const incoming = action.filters ?? {};
      const current = state.contextFilters[context] ?? {};
      const merged = clean({ ...current, ...incoming });

      return {
        ...state,
        contextFilters: { ...state.contextFilters, [context]: merged },
        activeSelectors: { ...state.activeSelectors, [context]: evaluate(merged) },
      };
    }

    case FILTER_ACTION_TYPES.UPDATE_VIEW: {
      const context: ContextKey = action.context ?? CONTEXT_TYPES.GLOBAL;
      const defaults = clean(action.filters ?? {});
      const view: View | null = action.view ?? null;

      return {
        ...state,
        contextFilters: { ...state.contextFilters, [context]: {} },
        activeSelectors: { ...state.activeSelectors, [context]: evaluate({}) },
        contextViewsDefaults: { ...state.contextViewsDefaults, [context]: defaults },
        contextViews: { ...state.contextViews, [context]: view },
      };
    }

    case FILTER_ACTION_TYPES.UPDATE_VIEW_FILTERS: {
      const context: ContextKey = action.context ?? CONTEXT_TYPES.GLOBAL;
      const defaults = clean(action.filters ?? {});
      return {
        ...state,
        contextViewsDefaults: { ...state.contextViewsDefaults, [context]: defaults },
      };
    }

    case FILTER_ACTION_TYPES.UPDATE_FILTER_OPTIONS: {
      const context: ContextKey = action.context ?? CONTEXT_TYPES.GLOBAL;
      const current = state.contextFilterOptions[context] ?? {};

      return {
        ...state,
        contextFilterOptions: {
          ...state.contextFilterOptions,
          [context]: {
            ...current,
            ...(action.filterOptions ?? {}),
          },
        },
      };
    }

    case FILTER_ACTION_TYPES.RESET_FILTERS: {
      const context: ContextKey = action.context ?? CONTEXT_TYPES.GLOBAL;

      const resetPeriod = toLatestYearDateRange(action.resetPeriod);
      const periodFilter = resetPeriod.length ? { period: resetPeriod } : {};

      if (state.contextViews[context] && Object.keys(state.contextViewsDefaults[context] ?? {}).length > 0) {
        return {
          ...state,
          contextFilters: { ...state.contextFilters, [context]: periodFilter },
          activeSelectors: { ...state.activeSelectors, [context]: evaluate(periodFilter) },
        };
      }

      const base = initState.contextFilters[context];
      const nextFilters = { ...base, ...periodFilter };

      return {
        ...state,
        contextFilters: { ...state.contextFilters, [context]: nextFilters },
        activeSelectors: { ...state.activeSelectors, [context]: evaluate(nextFilters) },
      };
    }

    case FILTER_ACTION_TYPES.RESET_VIEW: {
      const context: ContextKey = action.context ?? CONTEXT_TYPES.GLOBAL;
      const base = initState.contextFilters[context];

      return {
        ...state,
        contextFilters: { ...state.contextFilters, [context]: base },
        activeSelectors: { ...state.activeSelectors, [context]: evaluate(base) },
        contextViewsDefaults: { ...state.contextViewsDefaults, [context]: {} },
        contextViews: { ...state.contextViews, [context]: null },
        contextFilterOptions: {
          ...state.contextFilterOptions,
          [context]: initState.contextFilterOptions[context],
        },
      };
    }

    default:
      throw Error('Unknown action: ' + action.type);
  }
};

const contextsWithDates: ContextKey[] = [
  CONTEXT_TYPES.GLOBAL,
  CONTEXT_TYPES.FLOWMAP,
  CONTEXT_TYPES.BENCHMARK,
  CONTEXT_TYPES.DATASET,
];

export function FiltersProvider({ children }: FiltersProviderProps) {
  const { auth } = useAuth();
  const { setDateRangeMinMax } = useProfileSettings();
  const selectedGroupId = auth?.selectedGroup?.id;
  const [state, dispatch] = useReducer(filterReducer, initState);

  const didInitRef = useRef(false);

  // guard against stale values on LS
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    // Clear persistence
    resetPersistedFilterOptions({});

    // Clear in-memory filters (so hidden filters can't show stale context values)
    // If you only ever use GLOBAL here, keep only GLOBAL.
    (Object.values(CONTEXT_TYPES) as ContextKey[]).forEach((context) => {
      dispatch({ type: FILTER_ACTION_TYPES.RESET_FILTERS, context });
      dispatch({ type: FILTER_ACTION_TYPES.RESET_VIEW, context });
    });
  }, [dispatch]);

  useEffect(() => {
    if (!selectedGroupId) return;

    let isCurrent = true;

    const getPeriodData = async () => {
      try {
        const dt = await fetchPeriodData(selectedGroupId);

        if (!isCurrent) return;

        const selectedPeriod = toLatestYearDateRange(dt as DateRange);
        if (!selectedPeriod.length) return;

        persistOnLS('dateRangeMinMax', dt);
        setDateRangeMinMax(dt as DateRange);

        contextsWithDates.forEach((context) => {
          dispatch({
            type: FILTER_ACTION_TYPES.UPDATE,
            filters: { period: selectedPeriod },
            context,
          });
        });
      } catch (err) {
        console.error('FiltersContext update error:', err);
      }
    };

    getPeriodData();

    return () => {
      isCurrent = false;
    };
  }, [selectedGroupId, setDateRangeMinMax]);

  return (
    <FiltersContext.Provider value={state}>
      <FiltersDispatchContext.Provider value={dispatch}>{children}</FiltersDispatchContext.Provider>
    </FiltersContext.Provider>
  );
}

export function useFilters(): FilterState {
  const context = useContext(FiltersContext);
  if (!context) throw new Error('useFilters must be used within FiltersProvider');
  return context;
}

export function useFiltersDispatch(): Dispatch<FilterAction> {
  const context = useContext(FiltersDispatchContext);
  if (!context) throw new Error('useFiltersDispatch must be used within FiltersProvider');
  return context;
}
